import {Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {HttpErrorResponse} from "@angular/common/http";
import firebase from "firebase/compat/app";
import {onValue, ref} from "firebase/database";
import Unsubscribe = firebase.Unsubscribe;
import {ResourceAllocationTimeWindowExtModel} from "../../models/resource-allocation-time-window-ext.model";
import {ResourcesHttpService} from "../services/resources-http.service";

export interface ResourcesComponentState {
  resources: ResourceAllocationTimeWindowExtModel[];
  cursor?: string;
  loading: boolean;
  initialized: boolean;
  error: string | null;
}

@Injectable()
export class ResourcesComponentStore extends ComponentStore<ResourcesComponentState> {

  // Selectors
  readonly resources$ = this.select((state) => state.resources);
  readonly loading$ = this.select((state) => state.loading);
  readonly initialized$ = this.select((state) => state.initialized);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);


  vm$ = this.select({
    resources: this.resources$,
    loading: this.loading$,
    initialized: this.initialized$,
    error: this.error$,
    cursor: this.cursor$
  })

  private channelsUnsubscribeMap: Map<string, Unsubscribe> = new Map();

  constructor(
    @Inject(ResourcesHttpService) private resourcesHttpService: ResourcesHttpService,
    @Inject('firebaseService') private firebaseService
  ) {
    super({
      resources: [],
      cursor: '',
      loading: false,
      initialized: false,
      error: null,
    });
  }

  // Updaters
  readonly onSetResources = this.updater((state: ResourcesComponentState, value: {
    resources: ResourceAllocationTimeWindowExtModel[],
    cursor?: string
  }) => ({
    ...state,
    resources: [...state.resources, ...value.resources],
    cursor: value.cursor,
    loading: false,
    initialized: true,
    error: null,
  }));

  readonly updateResource = this.updater((state: ResourcesComponentState, updatedResource: ResourceAllocationTimeWindowExtModel) => ({
    ...state,
    resources: state.resources.map((resource: ResourceAllocationTimeWindowExtModel) => {
        return updatedResource.id === resource.id ? {...resource, ...updatedResource} : resource;
      }
    ),
  }));

  // Updaters
  readonly onResetResources = this.updater((state: ResourcesComponentState) => ({
    ...state,
    resources: [],
    cursor: null,
    loading: false,
    initialized: false,
    error: null,
  }));

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  // Effect for fetching resources
  readonly getResources = this.effect(
    (trigger$: Observable<any>) =>
      trigger$.pipe(
        tap(() => this.setLoading(true)), // Set loading state
        withLatestFrom(this.cursor$), // Combine with cursor from the state
        switchMap(([params, cursor]) => {
            return this.resourcesHttpService.getResources({...params, cursor: cursor || ''}).pipe(
              tap({
                next: (response) => {

                  //Subscribe to list
                  response.list?.forEach((resource: ResourceAllocationTimeWindowExtModel) => {
                    this.subscribe(resource.id);
                  })
                  return this.onSetResources({resources: response.list, cursor: response.cursor});
                },
                error: (err) => this.setError(err.message || 'Failed to fetch resources'),
              })
            )
          }
        )
      )
  );

  readonly resetResources = this.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.unSubscribeAll();
        this.onResetResources();
      })
    )
  );

  public unSubscribeAll(): void {
    for (const [id, channelUnsubscribe] of this.channelsUnsubscribeMap) {
      channelUnsubscribe();
    }
    this.channelsUnsubscribeMap.clear();
  }

  // Convert Firebase `onValue` into an observable
  public subscribe(id: string) {
    const channel = this.firebaseService.channel;
    const database = this.firebaseService.database;

    const path = `${channel.channelId}/${'resourceAllocationTimeWindow_changed'}/${id}`;
    const dbRef = ref(database, path);

    // Listen for real-time updates
    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        const resource: ResourceAllocationTimeWindowExtModel = snapshot.val();
        if (!!resource) {
          this.updateResource(resource);
        }
      }
    );

    // Cleanup when subscription is closed
    this.channelsUnsubscribeMap.set(id, unsubscribe);
  }

}
